import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Database, HardDriveDownload, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { STATE_NAMES, SUPPORTED_STATES } from '@shared/counties/index.js';
import { Page } from '@/components/AppShell';
import { AccessSettings } from '@/components/AccessSettings';
import { useAuth } from '@/lib/auth';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Field,
  Input,
  Label,
  LoadingState,
  Select,
} from '@/components/ui/primitives';
import {
  ConfirmButton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
} from '@/components/ui/overlays';
import { formatDateTime, formatRelative } from '@/lib/utils';
import { CALL_PROTOCOLS, FOLLOW_UP_TYPES, PIPELINE_STATUSES } from '@shared/enums.js';

export function SettingsPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'general';
  const { authEnabled, user } = useAuth();

  // While enforcement is off there is no signed-in user to check, and the owner
  // still needs to reach the switch that turns enforcement ON. Once it is on,
  // the server classifies every route behind this tab as admin-only anyway —
  // hiding it is courtesy, not the control.
  const showAccess = !authEnabled || user?.role === 'admin';

  return (
    <Page title="Settings" description="Everything here changes how LeadForge behaves. Changes are audited.">
      <div className="p-5 md:p-6">
        <Tabs
          value={tab}
          onValueChange={(value) => {
            params.set('tab', value);
            setParams(params, { replace: true });
          }}
        >
          <TabsList className="flex-wrap rounded-xl border border-border bg-card p-1 shadow-sm">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="reference">Categories & counties</TabsTrigger>
            <TabsTrigger value="calling">Calling</TabsTrigger>
            <TabsTrigger value="scoring">Scoring</TabsTrigger>
            <TabsTrigger value="issues">Website issues</TabsTrigger>
            <TabsTrigger value="imports">Imports & duplicates</TabsTrigger>
            <TabsTrigger value="jobs">Jobs</TabsTrigger>
            <TabsTrigger value="backups">Backups</TabsTrigger>
            {showAccess && <TabsTrigger value="access">Access</TabsTrigger>}
            <TabsTrigger value="audit">Audit log</TabsTrigger>
          </TabsList>

          <div className="pt-3">
            <TabsContent value="general"><GeneralTab /></TabsContent>
            <TabsContent value="reference"><ReferenceTab /></TabsContent>
            <TabsContent value="calling"><CallingTab /></TabsContent>
            <TabsContent value="scoring"><ScoringTab /></TabsContent>
            <TabsContent value="issues"><IssueTypesTab /></TabsContent>
            <TabsContent value="imports"><ImportSettingsTab /></TabsContent>
            <TabsContent value="jobs"><JobsTab /></TabsContent>
            <TabsContent value="backups"><BackupsTab /></TabsContent>
            {showAccess && <TabsContent value="access"><AccessSettings /></TabsContent>}
            <TabsContent value="audit"><AuditTab /></TabsContent>
          </div>
        </Tabs>
      </div>
    </Page>
  );
}

function useSettings() {
  return useQuery({ queryKey: ['settings'], queryFn: api.settings });
}

function useSaveSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) => api.updateSetting(key, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast.success('Saved');
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

function GeneralTab() {
  const settings = useSettings();
  const save = useSaveSetting();
  const general = settings.data?.settings['general.settings'] as
    | { timezone: string; defaultState: string; leadListPageSize: number }
    | undefined;
  const [form, setForm] = React.useState({ timezone: '', defaultState: '', leadListPageSize: 50 });

  React.useEffect(() => {
    if (general) setForm(general);
  }, [general]);

  if (settings.isLoading) return <LoadingState />;

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>General</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <Field
          label="Time zone"
          hint="Used for follow-up dates and 'today' calculations. Follow-ups are stored as calendar dates, so this only affects display and boundaries."
        >
          <Input value={form.timezone} onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))} />
        </Field>
        <Field label="Default state">
          {/* Constrained to the states whose county catalogue is supported. */}
          <Select
            value={form.defaultState}
            onChange={(e) => setForm((f) => ({ ...f, defaultState: e.target.value }))}
          >
            {SUPPORTED_STATES.map((code) => (
              <option key={code} value={code}>
                {STATE_NAMES[code]} ({code})
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Lead list page size">
          <Input
            type="number"
            min={10}
            max={500}
            value={form.leadListPageSize}
            onChange={(e) => setForm((f) => ({ ...f, leadListPageSize: Number(e.target.value) }))}
          />
        </Field>
        <Button size="sm" onClick={() => save.mutate({ key: 'general.settings', value: form })} loading={save.isPending}>
          Save
        </Button>
      </CardBody>
    </Card>
  );
}

function ReferenceTab() {
  const queryClient = useQueryClient();
  const categories = useQuery({ queryKey: ['categories-all'], queryFn: api.categories });
  const counties = useQuery({ queryKey: ['counties-all'], queryFn: api.counties });
  // Same cached ['settings'] query the General tab uses; no extra request.
  const settings = useSettings();
  const [newCategory, setNewCategory] = React.useState('');
  const [newCounty, setNewCounty] = React.useState('');
  // A county name alone is ambiguous once two states are supported.
  // Defaults from the saved general.settings.defaultState, which this page has
  // already loaded — no second settings query for it.
  const [newCountyState, setNewCountyState] = React.useState<string>('');
  const savedDefaultState = (
    settings.data?.settings['general.settings'] as { defaultState?: string } | undefined
  )?.defaultState;
  React.useEffect(() => {
    if (!newCountyState) {
      setNewCountyState(
        savedDefaultState && (SUPPORTED_STATES as readonly string[]).includes(savedDefaultState)
          ? savedDefaultState
          : SUPPORTED_STATES[0],
      );
    }
  }, [savedDefaultState, newCountyState]);
  const [countyFilter, setCountyFilter] = React.useState('');

  const addCategory = useMutation({
    mutationFn: () => api.createCategory({ name: newCategory.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories-all'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setNewCategory('');
      toast.success('Category added');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const addCounty = useMutation({
    // The state is chosen, not assumed: a county name alone is ambiguous now.
    mutationFn: () => api.createCounty({ name: newCounty.trim(), state: newCountyState }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['counties-all'] });
      queryClient.invalidateQueries({ queryKey: ['counties'] });
      setNewCounty('');
      toast.success('County added');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const removeCategory = useMutation({
    mutationFn: (id: number) => api.deleteCategory(id),
    onSuccess: (result: unknown) => {
      queryClient.invalidateQueries({ queryKey: ['categories-all'] });
      const r = result as { deactivated?: boolean; leadCount?: number };
      toast.success(r?.deactivated ? `In use by ${r.leadCount} leads — deactivated instead` : 'Category deleted');
    },
  });

  const removeCounty = useMutation({
    mutationFn: (id: number) => api.deleteCounty(id),
    onSuccess: (result: unknown) => {
      queryClient.invalidateQueries({ queryKey: ['counties-all'] });
      const r = result as { deactivated?: boolean; leadCount?: number };
      toast.success(r?.deactivated ? `In use by ${r.leadCount} leads — deactivated instead` : 'County deleted');
    },
  });

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Categories</CardTitle>
        </CardHeader>
        <CardBody className="space-y-2">
          <div className="flex gap-2">
            <Input
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="New category"
              onKeyDown={(e) => e.key === 'Enter' && newCategory.trim() && addCategory.mutate()}
            />
            <Button size="sm" disabled={!newCategory.trim()} onClick={() => addCategory.mutate()}>
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="max-h-96 space-y-1 overflow-y-auto">
            {categories.data?.map((c) => (
              <div key={c.id} className="flex items-center gap-2 rounded border border-border px-2 py-1 text-xs">
                <span className="flex-1 truncate">{c.name}</span>
                <span className="tabular-nums text-muted-foreground">{c.leadCount}</span>
                <ConfirmButton
                  title={`Delete “${c.name}”?`}
                  description="If leads use it, it is deactivated instead of deleted."
                  confirmLabel="Delete"
                  onConfirm={() => removeCategory.mutate(c.id)}
                >
                  <button className="text-muted-foreground hover:text-red-300">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </ConfirmButton>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Counties</CardTitle>
        </CardHeader>
        <CardBody className="space-y-2">
          <p className="text-xs text-muted-foreground">
            All 88 Ohio and 83 Michigan counties, from the U.S. Census Bureau county file. The two counts are leads
            located in the county and leads that serve it.
          </p>
          <div className="flex gap-2">
            <Input
              value={newCounty}
              onChange={(e) => setNewCounty(e.target.value)}
              placeholder="New county"
              onKeyDown={(e) => e.key === 'Enter' && newCounty.trim() && addCounty.mutate()}
            />
            <Select
              value={newCountyState}
              onChange={(e) => setNewCountyState(e.target.value)}
              className="w-28"
              aria-label="State"
            >
              {SUPPORTED_STATES.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </Select>
            <Button size="sm" disabled={!newCounty.trim()} onClick={() => addCounty.mutate()}>
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
          {/* 88 rows needs a filter; scrolling to find "Wyandot" is not a UI. */}
          <Input
            value={countyFilter}
            onChange={(e) => setCountyFilter(e.target.value)}
            placeholder="Filter counties…"
          />
          <div className="max-h-96 space-y-1 overflow-y-auto">
            {counties.data
              ?.filter((c) => c.name.toLowerCase().includes(countyFilter.trim().toLowerCase()))
              .map((c) => (
              <div key={c.id} className="flex items-center gap-2 rounded border border-border px-2 py-1 text-xs">
                <span className="flex-1 truncate">
                  {c.name} <span className="text-muted-foreground">{c.state}</span>
                  {c.fipsCode && (
                    <span className="ml-1 text-muted-foreground/70 tabular-nums">{c.fipsCode}</span>
                  )}
                </span>
                <span className="tabular-nums text-muted-foreground" title="Leads located here">
                  {c.leadCount}
                </span>
                <span className="tabular-nums text-muted-foreground/70" title="Leads serving here">
                  {c.serviceLeadCount ?? 0}
                </span>
                <ConfirmButton
                  title={`Delete ${c.name} County?`}
                  confirmLabel="Delete"
                  onConfirm={() => removeCounty.mutate(c.id)}
                >
                  <button className="text-muted-foreground hover:text-red-300">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </ConfirmButton>
              </div>
              ))}
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

function CallingTab() {
  const queryClient = useQueryClient();
  const settings = useSettings();
  const save = useSaveSetting();
  const outcomes = useQuery({ queryKey: ['call-outcomes'], queryFn: api.callOutcomes });

  const callSettings = settings.data?.settings['call.protocol'] as
    | { protocol: string; sipDomain: string | null }
    | undefined;
  const [protocol, setProtocol] = React.useState('tel');
  const [sipDomain, setSipDomain] = React.useState('');

  React.useEffect(() => {
    if (callSettings) {
      setProtocol(callSettings.protocol);
      setSipDomain(callSettings.sipDomain ?? '');
    }
  }, [callSettings]);

  const saveMapping = useMutation({
    mutationFn: ({ outcomeId, body }: { outcomeId: number; body: unknown }) =>
      api.updateOutcomeMapping(outcomeId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['call-outcomes'] });
      toast.success('Outcome behaviour updated');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const saveCooldown = useMutation({
    mutationFn: ({ outcomeId, body }: { outcomeId: number; body: unknown }) =>
      api.updateCooldown(outcomeId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['call-outcomes'] });
      toast.success('Cooldown updated');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (outcomes.isLoading) return <LoadingState />;

  return (
    <div className="space-y-3">
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Click-to-call</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <Field label="Protocol" hint="Copy-only puts the number on the clipboard instead of launching a dialer.">
            <Select value={protocol} onChange={(e) => setProtocol(e.target.value)}>
              {CALL_PROTOCOLS.map((p) => (
                <option key={p} value={p}>
                  {p === 'copy' ? 'copy-only' : `${p}:`}
                </option>
              ))}
            </Select>
          </Field>
          {protocol === 'sip' && (
            <Field label="SIP domain">
              <Input value={sipDomain} onChange={(e) => setSipDomain(e.target.value)} placeholder="pbx.local" />
            </Field>
          )}
          <Button
            size="sm"
            loading={save.isPending}
            onClick={() =>
              save.mutate({ key: 'call.protocol', value: { protocol, sipDomain: sipDomain || null } })
            }
          >
            Save
          </Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Outcome behaviour and cooldowns</CardTitle>
        </CardHeader>
        <CardBody>
          <p className="mb-2 text-xs text-muted-foreground">
            All outcome behaviour lives here — status changes, DNC, follow-up suggestions and cooldowns. Exactly
            one active mapping is allowed per outcome.
          </p>
          <table className="data-table">
            <thead>
              <tr>
                <th className="w-36">Outcome</th>
                <th className="w-40">Sets status</th>
                <th className="w-20">DNC</th>
                <th className="w-36">Suggests follow-up</th>
                <th className="w-20">In days</th>
                <th className="w-32">Cooldown</th>
              </tr>
            </thead>
            <tbody>
              {outcomes.data?.outcomes.map((outcome) => {
                const mapping = outcomes.data?.mappings.find((m) => m.outcomeId === outcome.id);
                const cooldown = outcomes.data?.cooldowns.find((c) => c.outcomeId === outcome.id);
                const update = (patch: Record<string, unknown>) =>
                  saveMapping.mutate({
                    outcomeId: outcome.id,
                    body: {
                      setPipelineStatus: mapping?.setPipelineStatus ?? null,
                      applyDnc: mapping?.applyDnc ?? false,
                      dncReason: mapping?.dncReason ?? null,
                      triggersCustomerConversion: mapping?.triggersCustomerConversion ?? false,
                      suggestFollowUpType: mapping?.suggestFollowUpType ?? null,
                      suggestFollowUpDays: mapping?.suggestFollowUpDays ?? null,
                      ...patch,
                    },
                  });

                return (
                  <tr key={outcome.id}>
                    <td className="font-medium">{outcome.name}</td>
                    <td>
                      <Select
                        value={mapping?.setPipelineStatus ?? ''}
                        onChange={(e) => update({ setPipelineStatus: e.target.value || null })}
                        className="h-6 text-2xs"
                      >
                        <option value="">— no change —</option>
                        {PIPELINE_STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </Select>
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={mapping?.applyDnc ?? false}
                        onChange={(e) => update({ applyDnc: e.target.checked })}
                        className="h-3.5 w-3.5 accent-[hsl(var(--primary))]"
                      />
                    </td>
                    <td>
                      <Select
                        value={mapping?.suggestFollowUpType ?? ''}
                        onChange={(e) => update({ suggestFollowUpType: e.target.value || null })}
                        className="h-6 text-2xs"
                      >
                        <option value="">— none —</option>
                        {FOLLOW_UP_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </Select>
                    </td>
                    <td>
                      <Input
                        type="number"
                        min={0}
                        value={mapping?.suggestFollowUpDays ?? ''}
                        onChange={(e) =>
                          update({ suggestFollowUpDays: e.target.value ? Number(e.target.value) : null })
                        }
                        className="h-6 text-2xs"
                      />
                    </td>
                    <td>
                      {cooldown?.useFollowUpDate ? (
                        <Tooltip content="Waits until the chosen follow-up date">
                          <Badge variant="outline">follow-up date</Badge>
                        </Tooltip>
                      ) : (
                        <Input
                          type="number"
                          min={0}
                          placeholder="minutes"
                          value={cooldown?.durationMinutes ?? ''}
                          onChange={(e) =>
                            saveCooldown.mutate({
                              outcomeId: outcome.id,
                              body: {
                                durationMinutes: e.target.value ? Number(e.target.value) : null,
                                useFollowUpDate: false,
                              },
                            })
                          }
                          className="h-6 text-2xs"
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </div>
  );
}

function ScoringTab() {
  const settings = useSettings();
  const save = useSaveSetting();
  const config = settings.data?.settings['scoring.config'] as Record<string, never> | undefined;
  const [draft, setDraft] = React.useState('');

  React.useEffect(() => {
    if (config) setDraft(JSON.stringify(config, null, 2));
  }, [config]);

  if (settings.isLoading) return <LoadingState />;

  return (
    <Card className="max-w-3xl">
      <CardHeader>
        <CardTitle>Scoring rules</CardTitle>
        <Badge variant="outline">version {(config as { version?: number })?.version ?? '—'}</Badge>
      </CardHeader>
      <CardBody className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Weights are plain numbers, documented in <code className="font-mono">docs/SCORING.md</code>. Only the
          five highest-weighted open findings count toward a score, with a per-category cap, so a pile of broken
          links cannot outrank one dead quote form. Raise the version when you change weights so old scores stay
          explainable.
        </p>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={26}
          className="w-full rounded-md border border-input bg-surface p-2 font-mono text-2xs"
          spellCheck={false}
        />
        <div className="flex gap-2">
          <Button
            size="sm"
            loading={save.isPending}
            onClick={() => {
              try {
                save.mutate({ key: 'scoring.config', value: JSON.parse(draft) });
              } catch {
                toast.error('That is not valid JSON');
              }
            }}
          >
            Save rules
          </Button>
          <Button variant="outline" size="sm" onClick={() => setDraft(JSON.stringify(config, null, 2))}>
            Reset
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function IssueTypesTab() {
  const queryClient = useQueryClient();
  const types = useQuery({ queryKey: ['issue-types'], queryFn: api.issueTypes });

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: unknown }) => api.updateIssueType(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issue-types'] });
      toast.success('Issue type updated');
    },
  });

  if (types.isLoading) return <LoadingState />;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Website issue types and weights</CardTitle>
        <span className="text-2xs text-muted-foreground">{types.data?.length} types</span>
      </CardHeader>
      <CardBody>
        <p className="mb-2 text-xs text-muted-foreground">
          Weight is the maximum points a finding of this type can contribute, before the confidence multiplier
          and the per-category cap.
        </p>
        <div className="max-h-[36rem] overflow-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th className="min-w-[16rem]">Label</th>
                <th className="w-48">Code</th>
                <th className="w-40">Category</th>
                <th className="w-28">Severity</th>
                <th className="w-24 text-right">Weight</th>
              </tr>
            </thead>
            <tbody>
              {types.data?.map((type) => (
                <tr key={type.id}>
                  <td>{type.label}</td>
                  <td className="font-mono text-2xs text-muted-foreground">{type.code}</td>
                  <td className="text-muted-foreground">{type.category.replace(/_/g, ' ')}</td>
                  <td>
                    <Badge variant="outline">{type.defaultSeverity}</Badge>
                  </td>
                  <td>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      defaultValue={type.scoreWeight}
                      onBlur={(e) => {
                        const value = Number(e.target.value);
                        if (value !== type.scoreWeight) update.mutate({ id: type.id, body: { scoreWeight: value } });
                      }}
                      className="h-6 text-right text-2xs"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}

function ImportSettingsTab() {
  const settings = useSettings();
  const save = useSaveSetting();
  const importSettings = settings.data?.settings['import.settings'] as
    | { watchedFolderEnabled: boolean; watchedFolderRoot: string; rawRowRetentionDays: number }
    | undefined;
  const duplicateSettings = settings.data?.settings['duplicate.settings'] as
    | { fuzzyAutoMergeThreshold: number; fuzzyReviewThreshold: number; maxCandidatesPerLead: number }
    | undefined;

  const [imports, setImports] = React.useState({ watchedFolderEnabled: false, watchedFolderRoot: '', rawRowRetentionDays: 90 });
  const [dupes, setDupes] = React.useState({ fuzzyAutoMergeThreshold: 0.95, fuzzyReviewThreshold: 0.78, maxCandidatesPerLead: 25 });

  React.useEffect(() => {
    if (importSettings) setImports(importSettings);
  }, [importSettings]);
  React.useEffect(() => {
    if (duplicateSettings) setDupes(duplicateSettings);
  }, [duplicateSettings]);

  const purge = useMutation({
    mutationFn: api.purgeRawImportRows,
    onSuccess: (result) => toast.success(`Purged raw data from ${result.purged} rows`),
  });

  if (settings.isLoading) return <LoadingState />;

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Imports</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <Field label="Watched folder root">
            <Input
              value={imports.watchedFolderRoot}
              onChange={(e) => setImports((f) => ({ ...f, watchedFolderRoot: e.target.value }))}
            />
          </Field>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={imports.watchedFolderEnabled}
              onChange={(e) => setImports((f) => ({ ...f, watchedFolderEnabled: e.target.checked }))}
              className="h-3.5 w-3.5 accent-[hsl(var(--primary))]"
            />
            Start the folder watcher when LeadForge starts
          </label>
          <Field label="Keep raw import rows for (days)" hint="0 keeps them indefinitely.">
            <Input
              type="number"
              min={0}
              value={imports.rawRowRetentionDays}
              onChange={(e) => setImports((f) => ({ ...f, rawRowRetentionDays: Number(e.target.value) }))}
            />
          </Field>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => save.mutate({ key: 'import.settings', value: imports })}>
              Save
            </Button>
            <Button variant="outline" size="sm" loading={purge.isPending} onClick={() => purge.mutate()}>
              Purge expired raw data now
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Duplicate thresholds</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <Field
            label="Review threshold"
            hint="Pairs scoring at or above this are proposed for review. 0.78 is the default."
          >
            <Input
              type="number"
              step="0.01"
              min={0}
              max={1}
              value={dupes.fuzzyReviewThreshold}
              onChange={(e) => setDupes((f) => ({ ...f, fuzzyReviewThreshold: Number(e.target.value) }))}
            />
          </Field>
          <Field label="Candidates considered per lead">
            <Input
              type="number"
              min={1}
              max={200}
              value={dupes.maxCandidatesPerLead}
              onChange={(e) => setDupes((f) => ({ ...f, maxCandidatesPerLead: Number(e.target.value) }))}
            />
          </Field>
          <Button size="sm" onClick={() => save.mutate({ key: 'duplicate.settings', value: dupes })}>
            Save
          </Button>
        </CardBody>
      </Card>
    </div>
  );
}

function JobsTab() {
  const queryClient = useQueryClient();
  const jobs = useQuery({ queryKey: ['jobs'], queryFn: api.jobs, refetchInterval: 3000 });
  const status = useQuery({ queryKey: ['job-status'], queryFn: api.jobStatus, refetchInterval: 3000 });
  const workerErrors = useQuery({ queryKey: ['worker-errors'], queryFn: api.workerErrors });

  const rescore = useMutation({
    mutationFn: api.startRescore,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      toast.success('Rescoring started');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const backfill = useMutation({
    mutationFn: api.startSummaryBackfill,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      toast.success('Summary backfill started');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rebuild = useMutation({
    mutationFn: api.rebuildSearch,
    onSuccess: (result) => toast.success(`Search index rebuilt: ${result.indexed} leads`),
  });

  return (
    <div className="space-y-3">
      <Card>
        <CardHeader>
          <CardTitle>Maintenance</CardTitle>
        </CardHeader>
        <CardBody className="flex flex-wrap gap-2">
          <Button size="sm" loading={rescore.isPending} onClick={() => rescore.mutate()}>
            <RefreshCw className="h-3.5 w-3.5" />
            Recalculate all lead scores
          </Button>
          <Button variant="outline" size="sm" loading={backfill.isPending} onClick={() => backfill.mutate()}>
            <RefreshCw className="h-3.5 w-3.5" />
            Rebuild website summaries
          </Button>
          <Button variant="outline" size="sm" loading={rebuild.isPending} onClick={() => rebuild.mutate()}>
            <Database className="h-3.5 w-3.5" />
            Rebuild search index
          </Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Job history</CardTitle>
          <span className="text-2xs text-muted-foreground">
            Watcher: {status.data?.watchedFolder.running ? 'running' : 'stopped'}
          </span>
        </CardHeader>
        <CardBody>
          {!jobs.data || jobs.data.length === 0 ? (
            <p className="text-xs text-muted-foreground">No jobs have run yet.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th className="w-48">Type</th>
                  <th className="w-28">Status</th>
                  <th className="w-40">Progress</th>
                  <th>Message</th>
                  <th className="w-36">Started</th>
                  <th className="w-24" />
                </tr>
              </thead>
              <tbody>
                {jobs.data.map((job) => (
                  <tr key={job.id}>
                    <td>{job.jobType.replace(/_/g, ' ')}</td>
                    <td>
                      <Badge
                        variant={
                          job.status === 'completed'
                            ? 'success'
                            : job.status === 'failed'
                              ? 'danger'
                              : job.status === 'running'
                                ? 'primary'
                                : 'default'
                        }
                      >
                        {job.status}
                      </Badge>
                    </td>
                    <td className="tabular-nums">
                      {job.processedItems.toLocaleString()} / {job.totalItems.toLocaleString()}
                    </td>
                    <td className="truncate text-muted-foreground">{job.errorMessage ?? job.message ?? '—'}</td>
                    <td className="text-muted-foreground">{job.startedAt ? formatRelative(job.startedAt) : '—'}</td>
                    <td>
                      {(job.status === 'running' || job.status === 'queued') && (
                        <Button variant="outline" size="xs" onClick={() => api.cancelJob(job.id)}>
                          Cancel
                        </Button>
                      )}
                      {job.status === 'failed' && (
                        <Button variant="outline" size="xs" onClick={() => api.resumeJob(job.id)}>
                          Resume
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {workerErrors.data && workerErrors.data.length > 0 && (
        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="text-red-300">Worker errors</CardTitle>
            <Button
              variant="outline"
              size="xs"
              onClick={async () => {
                await api.acknowledgeWorkerErrors();
                queryClient.invalidateQueries({ queryKey: ['worker-errors'] });
              }}
            >
              Acknowledge all
            </Button>
          </CardHeader>
          <CardBody className="space-y-1 text-xs">
            {workerErrors.data.map((err) => (
              <div key={err.id}>
                <span className="text-muted-foreground">{formatDateTime(err.createdAt)} — </span>
                {err.fileName && <span className="font-medium">{err.fileName}: </span>}
                {err.message}
              </div>
            ))}
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function BackupsTab() {
  const queryClient = useQueryClient();
  const backups = useQuery({ queryKey: ['backups'], queryFn: api.backups });
  const settings = useSettings();
  const save = useSaveSetting();

  const backupSettings = settings.data?.settings['backup.settings'] as
    | {
        destination: string;
        retentionCount: number;
        scheduledEnabled: boolean;
        scheduledIntervalHours: number;
      }
    | undefined;
  const [form, setForm] = React.useState({
    destination: '',
    retentionCount: 20,
    scheduledEnabled: true,
    scheduledIntervalHours: 24,
  });

  React.useEffect(() => {
    if (backupSettings) setForm(backupSettings);
  }, [backupSettings]);

  const create = useMutation({
    mutationFn: api.createBackup,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['backups'] });
      if (result.started) toast.success('Backup started. It runs in the background and appears in the list when it completes.', { duration: 8_000 });
      else toast.success(`Backup created and validated — ${result.validationMessage}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const drill = useMutation({
    mutationFn: () => api.verifyBackupRestore(),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['operations-reliability'] });
      toast.success(result.message, { duration: 12_000 });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const restore = useMutation({
    mutationFn: (fileName: string) => api.restoreBackup(fileName),
    onSuccess: (result) => {
      queryClient.invalidateQueries();
      toast.success(
        `Restored from ${result.restoredFrom} (${result.leadCount} leads). Safety copy: ${result.safetyBackup}`,
        { duration: 12_000 },
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: (fileName: string) => api.deleteBackup(fileName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backups'] });
      toast.success('Backup deleted');
    },
  });

  return (
    <div className="space-y-3">
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Backup settings</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <Field label="Destination folder">
            <Input value={form.destination} onChange={(e) => setForm((f) => ({ ...f, destination: e.target.value }))} />
          </Field>
          <Field label="Keep this many backups" hint="Safety copies taken before a restore are never pruned.">
            <Input
              type="number"
              min={1}
              value={form.retentionCount}
              onChange={(e) => setForm((f) => ({ ...f, retentionCount: Number(e.target.value) }))}
            />
          </Field>
          <Field
            label="Automatic backups"
            hint="LeadForge checks every few minutes and creates a validated snapshot whenever the interval is due."
          >
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={form.scheduledEnabled}
                onChange={(e) => setForm((f) => ({ ...f, scheduledEnabled: e.target.checked }))}
              />
              Keep automatic backups running
            </label>
          </Field>
          <Field
            label="Backup interval (hours)"
            hint="24 hours is the default. A stale or missing backup is created shortly after LeadForge starts."
          >
            <Input
              type="number"
              min={1}
              max={168}
              disabled={!form.scheduledEnabled}
              value={form.scheduledIntervalHours}
              onChange={(e) => setForm((f) => ({ ...f, scheduledIntervalHours: Number(e.target.value) }))}
            />
          </Field>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => save.mutate({ key: 'backup.settings', value: form })}>
              Save
            </Button>
            <Button variant="outline" size="sm" loading={create.isPending} onClick={() => create.mutate()}>
              <HardDriveDownload className="h-3.5 w-3.5" />
              Create a backup now
            </Button>
            <Button variant="outline" size="sm" loading={drill.isPending} onClick={() => drill.mutate()}>
              Test latest restore
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Backups</CardTitle>
        </CardHeader>
        <CardBody>
          <p className="mb-2 text-xs text-muted-foreground">
            Backups are written with SQLite's VACUUM INTO, never by copying the live file. Every one is opened and
            integrity-checked before it is listed as valid. A restore takes a safety copy first, stops the
            background workers, and is recorded in the audit log.
          </p>
          {backups.isLoading ? (
            <LoadingState />
          ) : !backups.data || backups.data.length === 0 ? (
            <p className="text-xs text-muted-foreground">No backups yet.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th className="min-w-[18rem]">File</th>
                  <th className="w-28 text-right">Size</th>
                  <th className="w-40">Created</th>
                  <th className="w-24">Validated</th>
                  <th className="w-52" />
                </tr>
              </thead>
              <tbody>
                {backups.data.map((backup) => (
                  <tr key={backup.fileName}>
                    <td className="font-mono text-2xs">{backup.fileName}</td>
                    <td className="text-right tabular-nums text-muted-foreground">
                      {(backup.byteSize / 1024 / 1024).toFixed(1)} MB
                    </td>
                    <td className="text-muted-foreground">{formatDateTime(backup.createdAt)}</td>
                    <td>
                      {backup.validated ? (
                        <Tooltip content={backup.validationMessage ?? ''}>
                          <Badge variant="success">valid</Badge>
                        </Tooltip>
                      ) : (
                        <Badge>unchecked</Badge>
                      )}
                    </td>
                    <td>
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="outline"
                          size="xs"
                          onClick={async () => {
                            const result = await api.validateBackup(backup.fileName);
                            queryClient.invalidateQueries({ queryKey: ['backups'] });
                            if (result.ok) toast.success(result.message);
                            else toast.error(result.message);
                          }}
                        >
                          Validate
                        </Button>
                        <ConfirmButton
                          title="Restore from this backup?"
                          description="Everything recorded since this backup will be lost. A safety copy of the current database is taken first, and background workers are stopped."
                          confirmLabel="Restore"
                          onConfirm={() => restore.mutate(backup.fileName)}
                        >
                          <Button variant="outline" size="xs">
                            Restore
                          </Button>
                        </ConfirmButton>
                        <ConfirmButton
                          title="Delete this backup file?"
                          confirmLabel="Delete"
                          onConfirm={() => remove.mutate(backup.fileName)}
                        >
                          <button className="px-1 text-muted-foreground hover:text-red-300">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </ConfirmButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function AuditTab() {
  const [page, setPage] = React.useState(1);
  const log = useQuery({ queryKey: ['audit-log', page], queryFn: () => api.auditLog(page) });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Audit log</CardTitle>
        <span className="text-2xs text-muted-foreground">{log.data?.total ?? 0} entries</span>
      </CardHeader>
      <CardBody>
        {log.isLoading ? (
          <LoadingState />
        ) : !log.data || log.data.rows.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nothing recorded yet.</p>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th className="w-40">When</th>
                  <th className="w-20">Actor</th>
                  <th className="w-48">Action</th>
                  <th className="w-32">Entity</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {log.data.rows.map((entry) => (
                  <tr key={entry.id}>
                    <td className="text-muted-foreground">{formatDateTime(entry.createdAt)}</td>
                    <td>
                      <Badge variant="outline">{entry.actorType}</Badge>
                    </td>
                    <td className="font-medium">{entry.action.replace(/_/g, ' ')}</td>
                    <td className="text-muted-foreground">
                      {entry.entityType ? `${entry.entityType}${entry.entityId ? ` #${entry.entityId}` : ''}` : '—'}
                    </td>
                    <td className="max-w-lg truncate font-mono text-2xs text-muted-foreground">
                      {entry.payloadJson ?? ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {log.data.pageCount > 1 && (
              <div className="mt-2 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  Page {page} of {log.data.pageCount}
                </span>
                <div className="flex gap-1">
                  <Button variant="outline" size="xs" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="xs"
                    disabled={page >= log.data.pageCount}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
}
